/**
 * RegenerateCodeModal - Admin Portal Component
 * 
 * Modal for regenerating a replacement promo code.
 * Sends email to customer with new code.
 */

import React, { useState } from 'react';
import './RegenerateCodeModal.css';

const LAMBDA_URL = 'https://fytckhfpoh5wgzodartzjtse640ueugs.lambda-url.ap-southeast-1.on.aws';

const RegenerateCodeModal = ({ isOpen, onClose, code, onSuccess }) => {
  const [customerEmail, setCustomerEmail] = useState('');
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [newCode, setNewCode] = useState(null);

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleRegenerate = async () => {
    if (customerEmail.trim() && !validateEmail(customerEmail.trim())) {
      setError('Please enter a valid email address');
      return;
    }

    if (!confirmed) {
      setError('Please confirm the regeneration action');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Get ID token from Cognito
      const { getIdToken } = await import('aws-amplify/auth');
      const { idToken } = (await getIdToken()).tokens;

      const response = await fetch(`${LAMBDA_URL}/regenerate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          code: code.code,
          customerEmail: customerEmail.trim() || undefined,
          reason: reason.trim() || undefined
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to regenerate code');
      }

      const data = await response.json();
      
      // Show new code
      setNewCode(data.newCode);
      
      // Success callback
      if (onSuccess) {
        onSuccess(data.newCode);
      }
    } catch (err) {
      console.error('Regenerate code error:', err);
      setError(err.message || 'Failed to regenerate code');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = () => {
    if (newCode) {
      navigator.clipboard.writeText(newCode.code);
    }
  };

  const handleClose = () => {
    setCustomerEmail('');
    setReason('');
    setConfirmed(false);
    setError(null);
    setNewCode(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">Regenerate Promo Code</h3>
          <button className="modal-close" onClick={handleClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="modal-body">
          {!newCode ? (
            <>
              <div className="modal-info">
                <div className="modal-info__icon">ℹ️</div>
                <div className="modal-info__text">
                  This will create a new promo code with the same parameters and mark the old code as replaced.
                </div>
              </div>

              <div className="modal-code-info">
                <div className="modal-code-info__label">Current Code:</div>
                <div className="modal-code-info__value">{code.code}</div>
              </div>

              <div className="modal-code-details">
                <div className="modal-code-details__item">
                  <span className="modal-code-details__label">Type:</span>
                  <span className="modal-code-details__value">{code.promoType}</span>
                </div>
                <div className="modal-code-details__item">
                  <span className="modal-code-details__label">Max Usage:</span>
                  <span className="modal-code-details__value">{code.maxUsage}</span>
                </div>
                <div className="modal-code-details__item">
                  <span className="modal-code-details__label">Expires:</span>
                  <span className="modal-code-details__value">
                    {code.expiryDate ? new Date(code.expiryDate).toLocaleDateString() : 'Never'}
                  </span>
                </div>
              </div>

              <div className="modal-form-group">
                <label className="modal-label" htmlFor="customer-email">
                  Customer Email (Optional)
                </label>
                <input
                  id="customer-email"
                  type="email"
                  className="modal-input"
                  placeholder="customer@example.com"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  disabled={loading}
                />
                <div className="modal-help-text">
                  If provided, customer will receive an email with the new code
                </div>
              </div>

              <div className="modal-form-group">
                <label className="modal-label" htmlFor="regenerate-reason">
                  Reason (Optional)
                </label>
                <textarea
                  id="regenerate-reason"
                  className="modal-textarea"
                  placeholder="Enter reason for regenerating code"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  disabled={loading}
                />
              </div>

              <div className="modal-checkbox-group">
                <label className="modal-checkbox">
                  <input
                    type="checkbox"
                    checked={confirmed}
                    onChange={(e) => setConfirmed(e.target.checked)}
                    disabled={loading}
                  />
                  <span>I understand this will mark the old code as replaced and create a new code</span>
                </label>
              </div>

              {error && (
                <div className="modal-error">
                  <strong>Error:</strong> {error}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="modal-success">
                <strong>Success!</strong> New promo code generated successfully.
              </div>

              <div className="modal-new-code">
                <div className="modal-new-code__label">New Promo Code:</div>
                <div className="modal-new-code__value">
                  <span className="modal-new-code__code">{newCode.code}</span>
                  <button
                    className="modal-new-code__copy"
                    onClick={handleCopyCode}
                    title="Copy code"
                  >
                    📋
                  </button>
                </div>
              </div>

              <div className="modal-code-details">
                <div className="modal-code-details__item">
                  <span className="modal-code-details__label">Type:</span>
                  <span className="modal-code-details__value">{newCode.promoType}</span>
                </div>
                <div className="modal-code-details__item">
                  <span className="modal-code-details__label">Max Usage:</span>
                  <span className="modal-code-details__value">{newCode.maxUsage}</span>
                </div>
                <div className="modal-code-details__item">
                  <span className="modal-code-details__label">Expires:</span>
                  <span className="modal-code-details__value">
                    {newCode.expiryDate ? new Date(newCode.expiryDate).toLocaleDateString() : 'Never'}
                  </span>
                </div>
              </div>

              {customerEmail && (
                <div className="modal-info">
                  <div className="modal-info__icon">✉️</div>
                  <div className="modal-info__text">
                    Email sent to <strong>{customerEmail}</strong> with the new code.
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          {!newCode ? (
            <>
              <button
                className="modal-button modal-button--secondary"
                onClick={handleClose}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                className="modal-button modal-button--primary"
                onClick={handleRegenerate}
                disabled={loading || !confirmed}
              >
                {loading ? 'Generating...' : 'Generate New Code'}
              </button>
            </>
          ) : (
            <button
              className="modal-button modal-button--primary"
              onClick={handleClose}
            >
              Done
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegenerateCodeModal;
